import {
    Box,
    Checkbox,
    Chip,
    Paper,
    Tooltip,
    Typography,
    styled,
    visuallyHiddenMixin,
} from '@lingoda/ui';
import type { ChangeEventHandler, ElementType, FocusEventHandler, ReactNode } from 'react';

interface OptionCardProps {
    title: string;
    subtitle?: string;
    emoji?: string;
    icon?: ReactNode;
    multiple?: boolean;
    checked: boolean;
    disabled?: boolean;
    badge?: { label: string; tooltip: string };
    name: string;
    value: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
}

export const OptionCard = (props: OptionCardProps) => {
    return (
        <StyledCard
            variant="outlined"
            checked={props.checked}
            disabled={props.disabled}
            component="label"
        >
            <HiddenInput
                type={props.multiple ? 'checkbox' : 'radio'}
                name={props.name}
                value={props.value}
                checked={props.checked}
                disabled={props.disabled}
                onChange={props.onChange}
                onBlur={props.onBlur}
            />
            {props.emoji && (
                <Typography component="span" aria-hidden sx={{ fontSize: 24, lineHeight: 1 }}>
                    {props.emoji}
                </Typography>
            )}
            {props.icon}
            <Box sx={{ flexGrow: 1, opacity: props.disabled ? 0.5 : undefined }}>
                <Typography variant="subtitle1">{props.title}</Typography>
                {props.subtitle && (
                    <Typography variant="body2" htmlColor="text.secondary">
                        {props.subtitle}
                    </Typography>
                )}
            </Box>
            {props.badge && (
                <Box sx={{ alignSelf: 'flex-start' }}>
                    <Tooltip
                        title={props.badge.tooltip}
                        placement="top"
                        arrow
                        enterTouchDelay={0}
                        display="inline-flex"
                    >
                        <Chip color="warning" label={props.badge.label} />
                    </Tooltip>
                </Box>
            )}
            {props.multiple && (
                <Checkbox
                    checked={props.checked}
                    tabIndex={-1}
                    inputProps={{ readOnly: true, 'aria-hidden': true }}
                    sx={{ pointerEvents: 'none' }}
                />
            )}
        </StyledCard>
    );
};

const StyledCard = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'checked' && prop !== 'disabled',
})<{ checked: boolean; disabled?: boolean; component?: ElementType }>(({
    theme,
    checked,
    disabled,
}) => {
    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        width: '100%',
        padding: theme.spacing(2, 3),
        borderRadius: theme.shape.borderRadiusSmall,
        cursor: 'pointer',
        ...(checked && {
            borderColor: theme.colors.border['accent-bold'],
            backgroundColor: theme.colors.background['accent-subtle'],
        }),
        ...(disabled && {
            cursor: 'default',
            backgroundColor: theme.colors.background['default-disabled'],
        }),
        '@supports selector(:focus-visible)': {
            '&:has(:focus-visible)': {
                outline: `2px solid ${theme.colors.border['accent-bold']}`,
                outlineOffset: 2,
            },
        },
    };
});

const HiddenInput = styled('input')(() => visuallyHiddenMixin());
